import { PulseEvent } from './types';

/**
 * The entity fields of a PulseEvent — what the event is *about*.
 *
 * Cinema-website programming falls into two cases and every call site has to handle both, so the
 * choice lives here rather than being re-decided per component (CIN-14405):
 *
 * - **film-backed** (`movieId` present) → sent as `movie_id`, which the API maps to
 *   `entity_type: 'movie'`. This feeds the four interest aggregate tables.
 * - **everything else** — special screenings, events, other programming → sent as
 *   `entity_type: 'content'`.
 *
 * Getting the first case wrong here is not fatal: ingestion re-resolves a content to its movie when
 * one exists, so a film-backed interaction is attributed to the film even if this component had no
 * `movieId` to hand (several call sites take `content` as an optional prop). What ingestion cannot
 * invent is a movie for genuinely film-less programming — those events reach `interest_event` and no
 * aggregate. See `docs/adr/0001-stop-recording-interest-in-non-film-content.md`.
 *
 * Passing neither is not representable: `contentId` is required, so an event always has a subject.
 */
export function trackedEntity({
    movieId,
    contentId
}: {
    movieId?: number | null;
    contentId: number;
}): Pick<PulseEvent, 'movie_id' | 'entity_type' | 'entity_id'> {
    if (movieId) {
        return { movie_id: movieId };
    }

    return { entity_type: 'content', entity_id: contentId };
}
